import type { AvatarConfig } from "@shared/types";

export type RoomPhase = "lobby" | "countdown" | "racing" | "results";

export interface MpPlayer {
  id: string;
  displayName: string;
  avatarConfig: AvatarConfig;
  /** Stable slot index assigned by host; drives lateral offset in the race. */
  slotIdx: number;
  ready: boolean;
  finished: boolean;
  finishTimeMs?: number;
  finishScore?: number;
  finishCorrect?: number;
  finishTotal?: number;
  isBot?: boolean;
}

export interface MpRoomState {
  code: string;
  hostId: string;
  lessonId: string;
  /** Shared seed → identical Path + obstacle layout for all players. */
  seed: number;
  phase: RoomPhase;
  players: MpPlayer[];
  /** Server timestamp (ms) when countdown started; race starts countdownStartedAt + 3000. */
  countdownStartedAt?: number;
}

export interface MpPositionTick {
  s: number;
  laneOffset: number;
  state: "run" | "jump" | "slide" | "hit" | "finished";
}

export type MpEvent =
  | { type: "state"; state: MpRoomState }
  | { type: "tick"; from: string; tick: MpPositionTick }
  | { type: "leave"; from: string }
  | { type: "error"; message: string };

export interface MpClient {
  /** Open or join a room. Returns the room state once joined. */
  join(opts: {
    code: string;
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: AvatarConfig };
    lessonId?: string;
    seed?: number;
  }): Promise<MpRoomState>;

  setReady(ready: boolean): void;
  startRace(): void;
  sendTick(tick: MpPositionTick): void;
  reportFinish(opts: {
    timeMs: number;
    score: number;
    correctCount: number;
    totalQuestions: number;
  }): void;

  on(event: "event", cb: (e: MpEvent) => void): () => void;
  leave(): void;
}

export function newRoomCode(): string {
  // 4 chars, unambiguous (no 0/O/I/1).
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 4; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}
